import { discoveryApi } from './api'
import { approximateCoordinates, currentCoordinates } from './geo'

/**
 * Per-user ambient device location. It is an approximate city-level center
 * (~100 m) kept in localStorage so discovery can query immediately on load and
 * the header can show the detected city before the next GPS fix. It is not an
 * explicit filter and never replaces the selected-city filter.
 */
export interface AmbientLocation {
  latitude: number
  longitude: number
  city: string | null
  detectedAt: number
}

export const AMBIENT_LOCATION_STORAGE_PREFIX = 'kuniri.discovery.location.v1'
export const AUTO_LOCATION_ATTEMPT_PREFIX =
  'kuniri.discovery.location.attempt.v1'

/** Use the stored fix for 30 minutes; afterwards refresh GPS in background. */
export const LOCATION_FRESHNESS_MS = 30 * 60_000
/** Do not prompt for automatic GPS more than once per five minutes/session. */
export const AUTO_LOCATION_INTERVAL_MS = 5 * 60_000

const IGNORED_CITY_LABELS = new Set(['la tua posizione'])

function storage(): Storage | null {
  if (typeof window === 'undefined') return null
  try {
    return window.localStorage
  } catch {
    return null
  }
}

function sessionStorage(): Storage | null {
  if (typeof window === 'undefined') return null
  try {
    return window.sessionStorage
  } catch {
    return null
  }
}

function roundCoordinate(value: number): number {
  return Math.round(value * 1000) / 1000
}

/** Trims a city name and drops the legacy placeholder used by the old app. */
export function normalizeCityName(value: unknown): string | null {
  if (typeof value !== 'string') return null
  const trimmed = value.trim()
  if (!trimmed) return null
  if (IGNORED_CITY_LABELS.has(trimmed.toLowerCase())) return null
  return trimmed
}

function isLatitude(value: unknown): value is number {
  return (
    typeof value === 'number' &&
    Number.isFinite(value) &&
    value >= -90 &&
    value <= 90
  )
}

function isLongitude(value: unknown): value is number {
  return (
    typeof value === 'number' &&
    Number.isFinite(value) &&
    value >= -180 &&
    value <= 180
  )
}

function isDetectedAt(value: unknown): value is number {
  return (
    typeof value === 'number' &&
    Number.isFinite(value) &&
    value > 0
  )
}

/** Validates and normalizes a stored fix; malformed data is ignored. */
export function normalizeAmbientLocation(
  input: unknown,
): AmbientLocation | null {
  if (!input || typeof input !== 'object') return null
  const value = input as Partial<AmbientLocation>
  if (!isLatitude(value.latitude) || !isLongitude(value.longitude)) {
    return null
  }
  if (value.city !== null && typeof value.city !== 'string') return null
  if (!isDetectedAt(value.detectedAt)) return null
  return {
    latitude: roundCoordinate(value.latitude),
    longitude: roundCoordinate(value.longitude),
    city: normalizeCityName(value.city),
    detectedAt: value.detectedAt,
  }
}

export function ambientLocationKey(userId: string): string {
  return `${AMBIENT_LOCATION_STORAGE_PREFIX}.${userId}`
}

function autoAttemptKey(userId: string): string {
  return `${AUTO_LOCATION_ATTEMPT_PREFIX}.${userId}`
}

export function readAmbientLocation(
  userId: string | null,
): AmbientLocation | null {
  if (!userId) return null
  const store = storage()
  if (!store) return null
  try {
    const raw = store.getItem(ambientLocationKey(userId))
    if (!raw) return null
    return normalizeAmbientLocation(JSON.parse(raw))
  } catch {
    return null
  }
}

export function writeAmbientLocation(
  userId: string | null,
  location: AmbientLocation,
): void {
  if (!userId) return
  const store = storage()
  if (!store) return
  const normalized = normalizeAmbientLocation(location)
  if (!normalized) return
  try {
    store.setItem(ambientLocationKey(userId), JSON.stringify(normalized))
  } catch {
    // Storage can be denied in private mode; the in-memory fix still works.
  }
}

export function isAmbientLocationFresh(
  location: AmbientLocation,
  now: number = Date.now(),
): boolean {
  const age = now - location.detectedAt
  return age >= 0 && age <= LOCATION_FRESHNESS_MS
}

export function readAutoLocationAttempt(
  userId: string | null,
): number | null {
  if (!userId) return null
  const store = sessionStorage()
  if (!store) return null
  try {
    const raw = store.getItem(autoAttemptKey(userId))
    if (!raw) return null
    const parsed = Number(raw)
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null
  } catch {
    return null
  }
}

/**
 * Session-scoped throttle for automatic GPS prompts. Returns true and records
 * the attempt when a new request is allowed. Manual detection bypasses this.
 */
export function claimAutoLocationAttempt(
  userId: string | null,
  now: number = Date.now(),
): boolean {
  if (!userId) return false
  const last = readAutoLocationAttempt(userId)
  if (
    last !== null &&
    now >= last &&
    now - last < AUTO_LOCATION_INTERVAL_MS
  ) {
    return false
  }
  const store = sessionStorage()
  if (store) {
    try {
      store.setItem(autoAttemptKey(userId), String(now))
    } catch {
      // Without a session guard the current attempt still proceeds.
    }
  }
  return true
}

/**
 * Requests a fresh foreground fix and reverse geocodes it. The coordinates
 * remain useful as a radius center when reverse geocoding is unavailable, so
 * that failure only leaves `city` empty.
 */
export async function captureAmbientLocation(
  now: number = Date.now(),
): Promise<AmbientLocation> {
  const coordinates = await currentCoordinates()
  const approximate = approximateCoordinates(coordinates)
  let city: string | null = null
  try {
    const reverse = await discoveryApi.reverseGeocode(
      coordinates.latitude,
      coordinates.longitude,
    )
    city = normalizeCityName(reverse.city)
  } catch {
    // Coordinates still work as a radius center without a city name.
  }
  return {
    latitude: approximate.latitude,
    longitude: approximate.longitude,
    city,
    detectedAt: now,
  }
}

export interface CityLabelSources {
  explicitCity?: string | null
  ambientCity?: string | null
  profileCity?: string | null
}

/**
 * Header precedence: explicit selected city, ambient detected city, profile
 * city, then the localized detection prompt. The legacy "La tua posizione"
 * placeholder is treated as missing.
 */
export function resolveCityLabel(
  { explicitCity, ambientCity, profileCity }: CityLabelSources,
  fallback: string,
): string {
  return (
    normalizeCityName(explicitCity) ??
    normalizeCityName(ambientCity) ??
    normalizeCityName(profileCity) ??
    fallback
  )
}
